import React, { useEffect, useRef, useState } from 'react';
import { VideoIcons } from '../../../../../../icons/icons';
import { Button } from '../../../../../../ui';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { InputDetails } from '../../../../../../utils/types/utility_types';
import { IS_IOS } from '../../assigned_assessments';
import commonStyles from '../../assigned_assessments.module.css';
import styles from './input_settings_modal.module.css';

type InputSettingsModalProps = {
    commenceAssessment: () => void,
    inputDetails: InputDetails,
    setInputDetails: (details: InputDetails) => void,
    setIsWarningModalOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    setIsModalOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void
};

const defaultCamera: SelectOptions = {
  value: 'default_camera_id',
  label: 'Default Camera',
};
const defaultMic: SelectOptions = {
  value: 'default_mic_id',
  label: 'Default Mic',
};

export const InputSettingsModal = ({
  commenceAssessment,
  inputDetails,
  setInputDetails,
  setIsWarningModalOpen,
  setIsModalOpen,
}: InputSettingsModalProps) => {
  const videoEl = useRef<HTMLVideoElement>(null);
  const [cameraList, setCameraList] = useState<SelectOptions[]>([]);
  const [micList, setMicList] = useState<SelectOptions[]>([]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const uniqueMics = new Map<string, SelectOptions>();
      const uniqueCameras = new Map<string, SelectOptions>();

      devices.forEach((device) => {
        // Create a normalized label for the device, removing any numbers in parentheses
        const normalizedLabel = device.label.replace(/ \(\d+\)$/, '').trim();

        if (device.kind === 'audioinput') {
          // Only add if we haven't seen this device label before
          if (!uniqueMics.has(normalizedLabel) && normalizedLabel) {
            uniqueMics.set(normalizedLabel, {
              value: device.deviceId,
              label: normalizedLabel,
            });
          }
        } else if (device.kind === 'videoinput') {
          // Only add if we haven't seen this device label before
          if (!uniqueCameras.has(normalizedLabel) && normalizedLabel) {
            uniqueCameras.set(normalizedLabel, {
              value: device.deviceId,
              label: normalizedLabel,
            });
          }
        }
      });

      // Update state with unique devices
      setMicList(Array.from(uniqueMics.values()));
      setCameraList(Array.from(uniqueCameras.values()));
    });
  }, []);

  useEffect(() => {
    if (!videoEl) {
      return;
    }

    let stream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let scriptProcessor: ScriptProcessorNode | null = null;

    navigator.mediaDevices
      .getUserMedia({
        audio: {
          deviceId: inputDetails.micId,
        },
        video: {
          deviceId: inputDetails.cameraId,
        },
      })
      .then((mediaStream) => {
        stream = mediaStream;
        audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(mediaStream);
        scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);
        scriptProcessor.onaudioprocess = function () {
          const array = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(array);
          const arraySum = array.reduce((a, value) => a + value, 0);
          const average = arraySum / array.length;
          volumeTracker(average);
        };

        let video = videoEl.current;
        if (video) {
          video.muted = true;
          video.srcObject = mediaStream;
          video.autoplay = true;
        }
      })
      .catch((error) => {
        console.error('❌ InputSettingsModal: Error accessing media devices:', error);
      });

    // Cleanup: Stop all tracks and close audio context when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (scriptProcessor) {
        scriptProcessor.disconnect();
      }

      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().then(() => {
        });
      }

      if (videoEl.current) {
        videoEl.current.srcObject = null;
      }
    };
  }, [videoEl, inputDetails]);

  const volumeTracker = (volume: number) => {
    const micVolume = document.getElementById('mic-volume');
    if (!micVolume) return;

    const pidList = Array.from(micVolume.children) as HTMLElement[];
    const pidListToColor = Math.round(volume / 10);
    const pidToColor = pidList.slice(0, pidListToColor);

    // Reset all bars to default state
    pidList.forEach((pid) => {
      pid.className =
                'flex-1 rounded-sm bg-gray-200 transition-colors duration-150';
    });

    // Color active bars with gradient
    pidToColor.forEach((pid, index) => {
      const intensity = ((index + 1) / pidToColor.length) * 100;
      pid.className = `flex-1 rounded-sm transition-colors duration-150 ${
        intensity < 50
          ? 'bg-blue-400'
          : intensity < 75
            ? 'bg-blue-500'
            : 'bg-blue-600'
      }`;
    });
  };

  return (
    <div className='max-w-3xl mx-auto pb-4'>
      { /* Header */ }
      <div className='text-center mb-3 md:mb-2'>
        <p className='text-gray-600 text-xs md:block hidden'>
          Configure your camera and microphone for the assessment
        </p>
      </div>

      { /* Main Content */ }
      <div className='grid grid-cols-1 gap-3 md:gap-4'>
        { /* Video Preview Section */ }
        <div className='bg-gray-50 rounded-lg p-2 md:p-3 border border-gray-200'>
          <div
              className='aspect-video max-w-sm md:max-w-md mx-auto relative rounded-lg overflow-hidden bg-gray-900 shadow-inner mb-1 md:mb-2'>
            { IS_IOS ? (
              <video
                  className='w-full h-full object-contain max-w-2xl'
                  ref={videoEl}
                  autoPlay={true}
                  playsInline={true}
                  muted={true}
              />
            ) : (
              <video className='w-full h-full object-cover' ref={videoEl}/>
            ) }
          </div>
          <p className='text-xs text-gray-500 text-center'>Camera Preview</p>
        </div>

        { /* Settings Section */ }
        <div className='md:space-x-4 space-y-2 md:space-y-0 flex flex-col md:flex-row'>
          { /* Camera Selection */ }
          <div className='bg-white p-2 md:p-3 w-full rounded-lg border border-gray-200'>
            <div className='flex items-center gap-2 mb-2 md:mb-3'>
              <div
                  className='w-7 h-7 md:w-8 md:h-8 rounded-full bg-blue-100 hidden md:flex items-center justify-center flex-shrink-0'>
                <VideoIcons className='w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600'/>
              </div>
              <div className='flex-1 min-w-0'>
                <h3 className='text-xs font-semibold text-gray-900'>
                  Camera Device
                </h3>
                <p className='text-xs text-gray-500 truncate hidden md:block'>
                  Select your preferred webcam
                </p>
              </div>
            </div>
            <CustomSelect
                label=''
                name='camera'
                options={cameraList}
                value={defaultCamera}
                onChange={(value: string) =>
                  setInputDetails({ ...inputDetails, cameraId: value })
                }
            />
          </div>

          { /* Microphone Selection */ }
          <div className='bg-white w-full p-2 md:p-3 rounded-lg border border-gray-200'>
            <div className='flex items-center gap-2 mb-2 md:mb-3'>
              <div
                  className='w-7 h-7 md:w-8 md:h-8 hidden md:flex items-center justify-center rounded-full bg-blue-100 flex-shrink-0'>
                <svg
                    className='w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z'
                  />
                </svg>
              </div>
              <div className='flex-1 min-w-0'>
                <h3 className='text-xs font-semibold text-gray-900'>
                  Microphone Device
                </h3>
                <p className='text-xs text-gray-500 truncate hidden md:block'>
                  Select your preferred microphone
                </p>
              </div>
            </div>
            <CustomSelect
                label=''
                name='microphone'
                options={micList}
                value={defaultMic}
                onChange={(value: string) =>
                  setInputDetails({ ...inputDetails, micId: value })
                }
            />

            { /* Volume Indicator */ }
            <div className='mt-2 md:mt-3'>
              <p className='text-xs text-gray-500 mb-1.5'>Microphone Level</p>
              <div className='flex gap-1 h-1.5' id='mic-volume'>
                { [...Array(10)].map((_, index) => (
                  <div
                      key={index}
                      className='flex-1 rounded-sm bg-gray-200 transition-colors duration-150'
                  />
                )) }
              </div>
            </div>
          </div>
        </div>
      </div>

      { /* Action Button */ }
      <div className='mt-4 md:mt-5 flex justify-center md:justify-end'>
        <Button
            onClick={() => {
              setIsModalOpen(false);
              setIsWarningModalOpen(true);
            }}

            variant='primary'
            label='Continue to Assessment'
            className='w-full md:w-auto'
        />
      </div>
    </div>
  );
};
